<button mat-icon-button (click)="openPanel()" #notificationsOrigin>
  @if (unreadCount > 0) {
    <span class="absolute top-0 right-0 left-0 z-10 flex h-3 items-center justify-center">
      <span
        class="ml-4 mt-2.5 flex h-4 min-w-4 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 px-1 text-xs font-medium text-indigo-50"
      >
        {{ unreadCount }}
      </span>
    </span>
  }
  <mat-icon [svgIcon]="'heroicons_outline:bell'" class="text-white"></mat-icon>
</button>

<ng-template #notificationsPanel>
  <div
    class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
  >
    <div class="flex flex-shrink-0 items-center bg-primary py-4 pr-4 pl-6 text-on-primary">
      <div class="-ml-1 mr-3 sm:hidden">
        <button mat-icon-button (click)="closePanel()">
          <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x'"></mat-icon>
        </button>
      </div>
      <div class="text-lg font-medium leading-10">
        {{ 'Notifications' | transloco }}
      </div>
      @if (canManageLegacyNotifications()) {
        <div class="ml-auto">
          <button
            mat-icon-button
            [matTooltip]="'Mark all as read' | transloco"
            [disabled]="unreadCount === 0"
            (click)="markAllAsRead()"
          >
            <mat-icon
              class="text-current icon-size-5"
              [svgIcon]="'heroicons_solid:mail-open'"
            ></mat-icon>
          </button>
        </div>
      }
    </div>

    <div
      class="bg-card relative flex flex-auto flex-col overflow-y-auto overflow-x-hidden sm:max-h-120"
    >
      @if (isLoading) {
        <div class="flex flex-auto items-center justify-center py-12 px-8">
          <mat-spinner diameter="32"></mat-spinner>
        </div>
      }
      @if (!isLoading) {
        @for (group of notificationGroups; track trackByGroup($index, group)) {
          <div
            class="sticky top-0 z-10 bg-gray-100 px-6 py-2 text-sm font-bold uppercase tracking-wide text-gray-800"
          >
            {{ group.date | date: 'MMM dd, y' }}
          </div>

          @for (
            notification of group.notifications;
            track trackByFn($index, notification);
            let i = $index;
            let first = $first
          ) {
            <div
              class="group flex min-w-0 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
              [ngClass]="{
                'border-t border-gray-200 dark:border-gray-700': !first,
                'bg-red-50/70 unread': !notification.seen,
                'bg-white': notification.seen
              }"
            >
              @if (notification.link) {
                <!-- Normal links -->
                @if (!notification.useRouter) {
                  <a
                    class="flex min-w-0 flex-auto cursor-pointer py-5 pl-6"
                    [href]="notification.link"
                  >
                    <ng-container
                      *ngTemplateOutlet="notificationContent; context: { $implicit: notification }"
                    ></ng-container>
                  </a>
                }
                <!-- Router links -->
                @if (notification.useRouter) {
                  <a
                    class="flex min-w-0 flex-auto cursor-pointer py-5 pl-6"
                    [routerLink]="notification.link"
                  >
                    <ng-container
                      *ngTemplateOutlet="notificationContent; context: { $implicit: notification }"
                    ></ng-container>
                  </a>
                }
              }

              <!-- Notification without a link -->
              @if (!notification.link) {
                <div
                  class="flex min-w-0 flex-auto py-5 pl-6"
                  [ngClass]="{
                    'cursor-pointer': isFootprintActivityNotification(notification)
                  }"
                  (click)="openNotification(notification)"
                >
                  <ng-container
                    *ngTemplateOutlet="notificationContent; context: { $implicit: notification }"
                  ></ng-container>
                </div>
              }

              <!-- Actions -->
              @if (canManageNotification(notification)) {
                <div class="relative my-5 mr-6 ml-2 flex flex-col">
                  <!-- Indicator -->
                  <button
                    class="h-6 min-h-6 w-6"
                    mat-icon-button
                    (click)="toggleRead(notification, i)"
                    matTooltip="{{
                      notification.seen
                        ? ('Mark as unread' | transloco)
                        : ('Mark as read' | transloco)
                    }}"
                  >
                    <span
                      class="h-2 w-2 rounded-full"
                      [ngClass]="{
                        'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                          notification.seen,
                        'bg-red-500': !notification.seen
                      }"
                    ></span>
                  </button>
                  <!-- Remove -->
                  @if (canRemoveNotification(notification)) {
                    <button
                      class="h-6 min-h-6 w-6 sm:opacity-0 sm:group-hover:opacity-100"
                      mat-icon-button
                      (click)="delete(notification, i)"
                      matTooltip="{{ 'Remove' | transloco }}"
                    >
                      <mat-icon class="icon-size-4" [svgIcon]="'heroicons_solid:x'"></mat-icon>
                    </button>
                  }
                </div>
              }
            </div>
          }
        }

        <!-- Notification content template -->
        <ng-template #notificationContent let-notification>
          <!-- Icon -->
          @if (notification.icon && !notification.image) {
            <div
              class="mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
              [ngClass]="{
                'bg-red-100 text-red-500': !notification.seen,
                'bg-gray-100 text-gray-500 dark:bg-gray-700': notification.seen
              }"
            >
              <mat-icon class="icon-size-5" [svgIcon]="notification.icon"></mat-icon>
            </div>
          }
          <!-- Title, description & time -->
          <div class="flex min-w-0 flex-auto flex-col overflow-hidden">
            @if (notification.title) {
              <div
                class="break-words"
                [ngClass]="{
                  'font-bold text-gray-900': !notification.seen,
                  'font-semibold text-gray-700': notification.seen
                }"
                [innerHTML]="notification.title"
              ></div>
            }
            @if (notification.factoryName) {
              <div class="mt-0.5 text-sm font-bold text-indigo-600">
                {{ notification.factoryName }}
              </div>
            }
            @if (notification.text) {
              <div
                class="line-clamp-2 break-words"
                [innerHTML]="notification.text"
                [matTooltip]="notification.text"
              ></div>
            }
            <div class="text-secondary mt-2 text-sm leading-none">
              {{ notification.createdAt | date: 'MMM dd, h:mm a' }}
            </div>
          </div>
        </ng-template>

        @if (!notificationGroups || !notificationGroups.length) {
          <div
            class="flex flex-auto flex-col items-center justify-center py-12 px-8 sm:justify-start"
          >
            <div
              class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100"
            >
              <mat-icon
                class="text-primary-500-700"
                [svgIcon]="'heroicons_outline:bell'"
              ></mat-icon>
            </div>
            <div class="mt-5 text-2xl font-semibold tracking-tight">
              {{ 'No notifications' | transloco }}
            </div>
            <div class="text-secondary mt-1 w-full max-w-60 text-center text-md">
              {{ 'Your notifications will appear here' | transloco }}.
            </div>
          </div>
        }
      }
    </div>
  </div>
</ng-template>
